// ============================================================
// FIT ANATOMY GRID — componente condiviso
// ============================================================
// Posiziona in: src/components/common/fitAnatomy/FitAnatomyGrid.tsx

import React, { useRef, useState } from 'react';
import { FitAnatomyResource } from '../../../types/course';
import { useShakaPlayer } from '../../../hooks/useShakaPlayer';
import './FitAnatomyGrid.css';

// ─── Helpers ─────────────────────────────────────────────────

function formatFileSize(kb: number): string {
  return kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

function getTypeIcon(type: FitAnatomyResource['type']): string {
  switch (type) {
    case 'pdf':
      return 'bi-file-earmark-pdf-fill';
    case 'audio':
      return 'bi-headphones';
    case 'video':
      return 'bi-play-circle-fill';
  }
}

function getTypeLabel(type: FitAnatomyResource['type']): string {
  switch (type) {
    case 'pdf':
      return 'PDF';
    case 'audio':
      return 'Audio';
    case 'video':
      return 'Video';
  }
}

// ─── Props ───────────────────────────────────────────────────

interface FitAnatomyGridProps {
  resources: FitAnatomyResource[];
  isAuthenticated: boolean;
  authToken?: string;
}

// ─── Video Player inline (HLS via useShakaPlayer) ────────────

interface VideoPlayerProps {
  src: string;
  title: string;
  authToken?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, title, authToken }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const { buffering, error } = useShakaPlayer({
    videoRef,
    source: { url: src, format: 'hls' },
    authToken,
  });

  return (
    <div className="fa-card__video-player">
      {buffering && (
        <div className="fa-card__video-buffering">
          <div
            className="spinner-border spinner-border-sm text-light"
            role="status"
          >
            <span className="visually-hidden">Caricamento…</span>
          </div>
        </div>
      )}
      {error && (
        <p className="fa-card__video-error">
          <i className="bi bi-exclamation-triangle me-1" />
          {error}
        </p>
      )}
      <video
        ref={videoRef}
        controls
        autoPlay
        playsInline
        aria-label={title}
        style={{ width: '100%', borderRadius: '0.5rem' }}
      />
    </div>
  );
};

// ─── Card singola ─────────────────────────────────────────────

interface CardProps {
  resource: FitAnatomyResource;
  isAuthenticated: boolean;
  authToken?: string;
  isPlaying: boolean;
  isVideoOpen: boolean;
  onPlayAudio: (id: string) => void;
  onOpenVideo: (id: string) => void;
}

const FitAnatomyCard: React.FC<CardProps> = ({
  resource,
  isAuthenticated,
  authToken,
  isPlaying,
  isVideoOpen,
  onPlayAudio,
  onOpenVideo,
}) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(resource.fileUrl, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resource.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[FitAnatomy] Download fallito:', err);
    } finally {
      setDownloading(false);
    }
  };

  const typeIcon = getTypeIcon(resource.type);
  const typeLabel = getTypeLabel(resource.type);

  return (
    <div className={`fa-card fa-card--${resource.type}`}>
      {/* Header card */}
      <div className="fa-card__header">
        <span
          className={`fa-card__type-icon bi ${typeIcon}`}
          aria-hidden="true"
        />
        <span className="fa-card__type-label">{typeLabel}</span>
        {resource.fileSizeKb && (
          <span className="fa-card__size">
            {formatFileSize(resource.fileSizeKb)}
          </span>
        )}
      </div>

      {/* Contenuto */}
      <div className="fa-card__body">
        <h3 className="fa-card__title">{resource.title}</h3>
        {resource.description && (
          <p className="fa-card__description">{resource.description}</p>
        )}
      </div>

      {/* Azioni */}
      <div className="fa-card__footer">
        {!isAuthenticated ? (
          <div className="fa-card__lock">
            <i className="bi bi-lock-fill" aria-hidden="true" />
            <span>Registrati per accedere</span>
          </div>
        ) : resource.type === 'pdf' ? (
          <button
            className="fa-card__btn fa-card__btn--primary"
            onClick={handleDownload}
            disabled={downloading}
            aria-label={`Scarica ${resource.title}`}
          >
            <i
              className={`bi ${downloading ? 'bi-hourglass-split' : 'bi-download'}`}
              aria-hidden="true"
            />
            {downloading ? 'Scaricando…' : 'Scarica'}
          </button>
        ) : resource.type === 'audio' ? (
          <button
            className={`fa-card__btn fa-card__btn--primary${isPlaying ? ' fa-card__btn--playing' : ''}`}
            onClick={() => onPlayAudio(resource.id)}
            aria-label={
              isPlaying
                ? `Chiudi player ${resource.title}`
                : `Ascolta ${resource.title}`
            }
          >
            <i
              className={`bi ${isPlaying ? 'bi-x-circle' : 'bi-play-circle-fill'}`}
              aria-hidden="true"
            />
            {isPlaying ? 'Chiudi player' : 'Ascolta'}
          </button>
        ) : (
          // Video HLS — player inline con useShakaPlayer
          <button
            className={`fa-card__btn fa-card__btn--primary${isVideoOpen ? ' fa-card__btn--playing' : ''}`}
            onClick={() => onOpenVideo(resource.id)}
            aria-label={
              isVideoOpen
                ? `Chiudi video ${resource.title}`
                : `Guarda ${resource.title}`
            }
          >
            <i
              className={`bi ${isVideoOpen ? 'bi-x-circle' : 'bi-play-circle-fill'}`}
              aria-hidden="true"
            />
            {isVideoOpen ? 'Chiudi video' : 'Guarda'}
          </button>
        )}
      </div>

      {/* Player audio inline */}
      {resource.type === 'audio' && isAuthenticated && isPlaying && (
        <div className="fa-card__audio-player">
          <audio
            controls
            autoPlay
            src={resource.fileUrl}
            aria-label={resource.title}
            style={{ width: '100%' }}
          >
            Il tuo browser non supporta l'audio HTML5.
          </audio>
        </div>
      )}

      {/* Player video inline HLS */}
      {resource.type === 'video' && isAuthenticated && isVideoOpen && (
        <VideoPlayer
          src={resource.fileUrl}
          title={resource.title}
          authToken={authToken}
        />
      )}
    </div>
  );
};

// ─── Grid principale ──────────────────────────────────────────

const FitAnatomyGrid: React.FC<FitAnatomyGridProps> = ({
  resources,
  isAuthenticated,
  authToken,
}) => {
  const [activeAudioId, setActiveAudioId] = useState<string | null>(null);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  const handlePlayAudio = (id: string) => {
    setActiveAudioId((prev) => (prev === id ? null : id));
  };

  const handleOpenVideo = (id: string) => {
    setActiveVideoId((prev) => (prev === id ? null : id));
  };

  if (resources.length === 0) {
    return (
      <div className="fa-empty">
        <i className="bi bi-inbox fa-empty__icon" aria-hidden="true" />
        <p>Nessuna risorsa disponibile al momento.</p>
      </div>
    );
  }

  return (
    <div className="fa-grid">
      {resources.map((resource) => (
        <FitAnatomyCard
          key={resource.id}
          resource={resource}
          isAuthenticated={isAuthenticated}
          authToken={authToken}
          isPlaying={activeAudioId === resource.id}
          isVideoOpen={activeVideoId === resource.id}
          onPlayAudio={handlePlayAudio}
          onOpenVideo={handleOpenVideo}
        />
      ))}
    </div>
  );
};

export default FitAnatomyGrid;
